/**
 * physics.js — Rapier wrapper: fixed-step world, body factory, raycasts,
 * contact-force events, per-body gravity scale.
 *
 * Rapier ships as WASM. `@dimforge/rapier3d-compat` bundles the binary as
 * base64 inside the JS, so there is no separate .wasm fetch to configure — but
 * `await RAPIER.init()` MUST complete before any other Rapier call, which is
 * why `init()` is async and everything else asserts on `isReady()`.
 *
 * Bodies are created through `addBody(mesh, desc)`, which returns a handle
 * pairing the Rapier body with its Three.js mesh. `syncMeshes(alpha)` writes
 * interpolated transforms back onto the meshes each rendered frame, so a 72 Hz
 * simulation renders smoothly at any display rate.
 *
 * @module core/physics
 */

import * as THREE from 'three';
import {
  DEFAULT_GRAVITY,
  PHYSICS_DT,
  COLLISION,
  EVENTS,
} from '../constants.js';
import { bus } from './events.js';

/** @type {import('@dimforge/rapier3d-compat')|null} */
let RAPIER = null;
/** @type {any} */
let world = null;
/** @type {any} */
let eventQueue = null;

let ready = false;
let nextId = 1;

/** @type {Map<number, BodyHandle>} */
const handles = new Map();
/** Collider handle → BodyHandle, for resolving contact events. */
const byCollider = new Map();

/** Scratch objects — allocating in the step loop is how you lose frames. */
const _v3 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _v3b = new THREE.Vector3();

/**
 * A physics body paired with its render mesh.
 *
 * Never construct one directly — use `physics.addBody()`.
 */
export class BodyHandle {
  /**
   * @param {number} id
   * @param {any} body Rapier RigidBody.
   * @param {any} collider Rapier Collider.
   * @param {THREE.Object3D|null} mesh
   * @param {object} opts
   */
  constructor(id, body, collider, mesh, opts) {
    this.id = id;
    this.body = body;
    this.collider = collider;
    this.mesh = mesh || null;
    /** Free-form label so a stall can bulk-remove everything it spawned. */
    this.tag = opts.tag || null;
    /** Anything the owning system wants to hang off the body. */
    this.userData = opts.userData || {};
    this.kind = opts.kind || 'dynamic';
    this.mass = opts.mass || 0;
    this.removed = false;

    /** Gravity multiplier (GDD 5.4 per-stall gravityScale). */
    this._gravityScale = 1;
    /** True when we must emulate gravity scale with per-step forces. */
    this._manualGravity = false;

    // Interpolation state: previous and current step transforms.
    this._prevPos = new THREE.Vector3();
    this._prevQuat = new THREE.Quaternion();
    this._currPos = new THREE.Vector3();
    this._currQuat = new THREE.Quaternion();
    this.captureTransform();
    this._prevPos.copy(this._currPos);
    this._prevQuat.copy(this._currQuat);
  }

  /** Read the body's transform into the interpolation buffers. */
  captureTransform() {
    const t = this.body.translation();
    const r = this.body.rotation();
    this._prevPos.copy(this._currPos);
    this._prevQuat.copy(this._currQuat);
    this._currPos.set(t.x, t.y, t.z);
    this._currQuat.set(r.x, r.y, r.z, r.w);
  }

  /**
   * Write the (interpolated) physics transform onto the mesh.
   * @param {number} alpha 0–1 blend between the previous and current step.
   */
  syncMesh(alpha) {
    if (!this.mesh || this.removed) return;
    // Fixed bodies never move, and their collider is often deliberately offset
    // from the mesh origin (a shelf's collider sits at its top surface, not its
    // feet). Writing the body transform back would teleport the mesh.
    if (this.kind === 'fixed') return;
    this.mesh.position.lerpVectors(this._prevPos, this._currPos, alpha);
    this.mesh.quaternion.copy(this._prevQuat).slerp(this._currQuat, alpha);
  }

  /** @returns {THREE.Vector3} World position (fresh vector). */
  position() {
    const t = this.body.translation();
    return new THREE.Vector3(t.x, t.y, t.z);
  }

  /** @returns {THREE.Vector3} Linear velocity (fresh vector). */
  velocity() {
    const v = this.body.linvel();
    return new THREE.Vector3(v.x, v.y, v.z);
  }

  /** @param {THREE.Vector3|{x,y,z}} v */
  setPosition(v) {
    this.body.setTranslation({ x: v.x, y: v.y, z: v.z }, true);
    this.captureTransform();
    this._prevPos.copy(this._currPos);
    this._prevQuat.copy(this._currQuat);
    this.syncMesh(1);
  }

  /** @param {THREE.Quaternion} q */
  setRotation(q) {
    this.body.setRotation({ x: q.x, y: q.y, z: q.z, w: q.w }, true);
    this.captureTransform();
    this._prevQuat.copy(this._currQuat);
  }

  /** @param {THREE.Vector3|{x,y,z}} v */
  setVelocity(v) {
    this.body.setLinvel({ x: v.x, y: v.y, z: v.z }, true);
  }

  /** @param {THREE.Vector3|{x,y,z}} v */
  setAngularVelocity(v) {
    this.body.setAngvel({ x: v.x, y: v.y, z: v.z }, true);
  }

  /** Stop dead. */
  freeze() {
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }

  /**
   * Set the per-body gravity multiplier (GDD 5.4).
   *
   * Rapier exposes `RigidBody.setGravityScale` natively, so that is the path
   * taken. On a build where the method is missing we fall back to applying
   * (scale − 1) · g · mass as an explicit force every step, which produces the
   * same trajectory.
   *
   * @param {number} scale
   */
  setGravityScale(scale) {
    this._gravityScale = scale;
    if (typeof this.body.setGravityScale === 'function') {
      this.body.setGravityScale(scale, true);
      this._manualGravity = false;
    } else {
      this._manualGravity = scale !== 1;
    }
  }

  /** @returns {number} */
  gravityScale() {
    return this._gravityScale;
  }

  /** Applied once per physics step when native gravity scaling is unavailable. */
  applyManualGravity() {
    if (!this._manualGravity) return;
    const m = this.body.mass();
    if (m <= 0) return;
    const extra = (this._gravityScale - 1) * DEFAULT_GRAVITY * m;
    this.body.addForce({ x: 0, y: -extra, z: 0 }, true);
  }

  /** Switch between dynamic and fixed at runtime (used while an object is held). */
  setKinematic(on) {
    if (!RAPIER) return;
    this.body.setBodyType(
      on ? RAPIER.RigidBodyType.KinematicPositionBased : RAPIER.RigidBodyType.Dynamic,
      true,
    );
  }

  /** Remove from the world and detach from the scene graph. */
  remove() {
    removeBody(this);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Lifecycle
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Load the Rapier WASM module and build the world. Idempotent.
 * @returns {Promise<void>}
 */
export async function init() {
  if (ready) return;
  const mod = await import('@dimforge/rapier3d-compat');
  RAPIER = mod.default && mod.default.init ? mod.default : mod;
  await RAPIER.init();

  world = new RAPIER.World({ x: 0, y: -DEFAULT_GRAVITY, z: 0 });
  world.timestep = PHYSICS_DT;
  eventQueue = new RAPIER.EventQueue(true);

  ready = true;
  console.log('[physics] Rapier ready @', (1 / PHYSICS_DT).toFixed(0), 'Hz');
}

/** @returns {boolean} */
export function isReady() {
  return ready;
}

/** @returns {any} The raw RAPIER namespace (null before init). */
export function getRAPIER() {
  return RAPIER;
}

/** @returns {any} The Rapier World (null before init). */
export function getWorld() {
  return world;
}

/** @returns {number} Live body count. */
export function bodyCount() {
  return handles.size;
}

/** @returns {BodyHandle[]} Every live handle. */
export function allBodies() {
  return [...handles.values()];
}

// ─────────────────────────────────────────────────────────────────────────────
// Stepping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Advance the simulation by exactly one fixed timestep and drain events.
 * The engine calls this; gameplay code should not.
 */
export function step() {
  if (!ready) return;

  for (const h of handles.values()) {
    if (h._manualGravity) h.applyManualGravity();
  }

  world.step(eventQueue);

  for (const h of handles.values()) h.captureTransform();

  drainEvents();
}

/** Convert Rapier's event queue into bus events. */
function drainEvents() {
  eventQueue.drainContactForceEvents((event) => {
    const a = byCollider.get(event.collider1());
    const b = byCollider.get(event.collider2());
    const force = event.totalForceMagnitude();
    const payload = { a, b, force, maxForce: event.maxForceMagnitude?.() ?? force };
    bus.emit(EVENTS.CONTACT_FORCE, payload);
    for (const fn of contactListeners) fn(payload);
  });

  eventQueue.drainCollisionEvents((h1, h2, started) => {
    const a = byCollider.get(h1);
    const b = byCollider.get(h2);
    if (!a && !b) return;
    bus.emit(EVENTS.COLLISION, { a, b, started });
  });
}

/** @type {Set<Function>} */
const contactListeners = new Set();

/**
 * Subscribe to contact-force events. Coconut Shy's "force > 8 N" rule
 * (GDD 6.2.2) is the reason this exists.
 * @param {(e: {a?: BodyHandle, b?: BodyHandle, force: number}) => void} fn
 * @returns {() => void} Unsubscribe.
 */
export function onContactForce(fn) {
  contactListeners.add(fn);
  return () => contactListeners.delete(fn);
}

/**
 * Write interpolated transforms onto every mesh.
 * @param {number} alpha 0–1 fraction through the current physics step.
 */
export function syncMeshes(alpha) {
  for (const h of handles.values()) h.syncMesh(alpha);
}

// ─────────────────────────────────────────────────────────────────────────────
// Body creation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} BodyDesc
 * @property {'dynamic'|'fixed'|'kinematic'} [type='dynamic']
 * @property {'ball'|'box'|'capsule'|'cylinder'|'cone'|'trimesh'} [shape='box']
 * @property {number} [radius] For ball/capsule/cylinder/cone.
 * @property {number} [halfHeight] For capsule/cylinder/cone.
 * @property {{x,y,z}|number[]} [halfExtents] For box.
 * @property {number} [mass] Explicit mass in kg. Omit to use density.
 * @property {number} [density]
 * @property {number} [friction=0.5]
 * @property {number} [restitution=0.2]
 * @property {number} [linearDamping=0]
 * @property {number} [angularDamping=0.05]
 * @property {number} [gravityScale=1]
 * @property {boolean} [ccd=false] Continuous collision — needed for fast balls.
 * @property {boolean} [sensor=false]
 * @property {number} [groups] Interaction groups; defaults by type.
 * @property {boolean} [events=false] Emit contact-force events for this body.
 * @property {number} [contactForceThreshold=1]
 * @property {THREE.Vector3|{x,y,z}} [position] Overrides the mesh position.
 * @property {THREE.Quaternion} [rotation] Overrides the mesh quaternion.
 * @property {string} [tag] Ownership label for bulk removal.
 * @property {object} [userData]
 */

/**
 * Create a rigid body + collider, optionally bound to a mesh.
 *
 * If `mesh` is provided its current world transform seeds the body, and the
 * mesh is thereafter driven by physics. Pass `position`/`rotation` in the desc
 * to override.
 *
 * @param {THREE.Object3D|null} mesh
 * @param {BodyDesc} [desc]
 * @returns {BodyHandle}
 */
export function addBody(mesh, desc = {}) {
  if (!ready) throw new Error('physics.addBody() before init()');

  const type = desc.type || 'dynamic';

  // Seed the transform from the mesh's world matrix unless overridden.
  if (mesh) {
    mesh.updateWorldMatrix(true, false);
    mesh.getWorldPosition(_v3);
    mesh.getWorldQuaternion(_q);
  } else {
    _v3.set(0, 0, 0);
    _q.identity();
  }
  if (desc.position) _v3.set(desc.position.x, desc.position.y, desc.position.z);
  if (desc.rotation) _q.copy(desc.rotation);

  let bodyDesc;
  if (type === 'fixed') bodyDesc = RAPIER.RigidBodyDesc.fixed();
  else if (type === 'kinematic') bodyDesc = RAPIER.RigidBodyDesc.kinematicPositionBased();
  else bodyDesc = RAPIER.RigidBodyDesc.dynamic();

  bodyDesc.setTranslation(_v3.x, _v3.y, _v3.z);
  bodyDesc.setRotation({ x: _q.x, y: _q.y, z: _q.z, w: _q.w });
  if (desc.linearDamping !== undefined) bodyDesc.setLinearDamping(desc.linearDamping);
  bodyDesc.setAngularDamping(desc.angularDamping ?? 0.05);
  if (desc.ccd) bodyDesc.setCcdEnabled(true);

  const body = world.createRigidBody(bodyDesc);

  const colliderDesc = buildColliderDesc(desc);
  if (desc.mass !== undefined) colliderDesc.setMass(desc.mass);
  else if (desc.density !== undefined) colliderDesc.setDensity(desc.density);
  colliderDesc.setFriction(desc.friction ?? 0.5);
  colliderDesc.setRestitution(desc.restitution ?? 0.2);
  if (desc.sensor) colliderDesc.setSensor(true);

  const groups = desc.groups ?? defaultGroups(type, desc.sensor);
  colliderDesc.setCollisionGroups(groups);
  colliderDesc.setSolverGroups(groups);

  if (desc.events || desc.sensor) {
    colliderDesc.setActiveEvents(
      RAPIER.ActiveEvents.COLLISION_EVENTS | RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS,
    );
    colliderDesc.setContactForceEventThreshold(desc.contactForceThreshold ?? 1);
  }

  const collider = world.createCollider(colliderDesc, body);

  const handle = new BodyHandle(nextId++, body, collider, mesh, {
    tag: desc.tag,
    userData: desc.userData,
    kind: type,
    mass: desc.mass ?? 0,
  });

  if (desc.gravityScale !== undefined && desc.gravityScale !== 1) {
    handle.setGravityScale(desc.gravityScale);
  }

  handles.set(handle.id, handle);
  byCollider.set(collider.handle, handle);
  return handle;
}

/**
 * Pick the right ColliderDesc for the requested shape.
 * @param {BodyDesc} desc
 */
function buildColliderDesc(desc) {
  const shape = desc.shape || 'box';
  switch (shape) {
    case 'ball':
      return RAPIER.ColliderDesc.ball(desc.radius ?? 0.1);
    case 'capsule':
      return RAPIER.ColliderDesc.capsule(desc.halfHeight ?? 0.2, desc.radius ?? 0.1);
    case 'cylinder':
      return RAPIER.ColliderDesc.cylinder(desc.halfHeight ?? 0.1, desc.radius ?? 0.1);
    case 'cone':
      return RAPIER.ColliderDesc.cone(desc.halfHeight ?? 0.1, desc.radius ?? 0.1);
    case 'trimesh': {
      const { vertices, indices } = desc;
      return RAPIER.ColliderDesc.trimesh(vertices, indices);
    }
    case 'box':
    default: {
      const he = normaliseHalfExtents(desc.halfExtents);
      return RAPIER.ColliderDesc.cuboid(he.x, he.y, he.z);
    }
  }
}

function normaliseHalfExtents(he) {
  if (!he) return { x: 0.5, y: 0.5, z: 0.5 };
  if (Array.isArray(he)) return { x: he[0], y: he[1], z: he[2] };
  return { x: he.x, y: he.y, z: he.z };
}

function defaultGroups(type, sensor) {
  if (sensor) return COLLISION.SENSOR;
  return type === 'fixed' ? COLLISION.STATIC : COLLISION.LOCAL_DYNAMIC;
}

/**
 * Remove a body from the world. Its mesh is detached from its parent but not
 * disposed — the owning system decides whether geometry is reused.
 * @param {BodyHandle} handle
 */
export function removeBody(handle) {
  if (!handle || handle.removed) return;
  handle.removed = true;
  byCollider.delete(handle.collider.handle);
  handles.delete(handle.id);
  try {
    world.removeRigidBody(handle.body);
  } catch (err) {
    console.warn('[physics] removeRigidBody failed:', err);
  }
  if (handle.mesh && handle.mesh.parent) handle.mesh.parent.remove(handle.mesh);
}

/**
 * Remove every body carrying a tag. This is the cleanup discipline that keeps
 * three consecutive rounds from leaking bodies.
 * @param {string} tag
 * @returns {number} How many were removed.
 */
export function removeByTag(tag) {
  let n = 0;
  for (const h of [...handles.values()]) {
    if (h.tag === tag) {
      removeBody(h);
      n++;
    }
  }
  return n;
}

/** Tear the whole world down (used by hard resets). */
export function clear() {
  for (const h of [...handles.values()]) removeBody(h);
  handles.clear();
  byCollider.clear();
}

// ─────────────────────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cast a ray. Used by teleport arcs and the Duck Gallery's cork rifle.
 * @param {THREE.Vector3|{x,y,z}} origin
 * @param {THREE.Vector3|{x,y,z}} direction Must be normalised.
 * @param {number} [maxDistance=50]
 * @param {{ groups?: number, solid?: boolean, exclude?: BodyHandle }} [opts]
 * @returns {{ handle: BodyHandle|null, distance: number, point: THREE.Vector3,
 *             normal: THREE.Vector3 }|null}
 */
export function raycast(origin, direction, maxDistance = 50, opts = {}) {
  if (!ready) return null;
  const ray = new RAPIER.Ray(
    { x: origin.x, y: origin.y, z: origin.z },
    { x: direction.x, y: direction.y, z: direction.z },
  );
  const hit = world.castRayAndGetNormal(
    ray,
    maxDistance,
    opts.solid !== false,
    undefined,
    opts.groups,
    opts.exclude ? opts.exclude.collider : undefined,
    opts.exclude ? opts.exclude.body : undefined,
  );
  if (!hit) return null;
  const d = hit.timeOfImpact;
  const p = ray.pointAt(d);
  return {
    handle: byCollider.get(hit.collider.handle) || null,
    distance: d,
    point: new THREE.Vector3(p.x, p.y, p.z),
    normal: new THREE.Vector3(hit.normal.x, hit.normal.y, hit.normal.z),
  };
}

/**
 * Find dynamic bodies whose centre lies within `radius` of a point.
 * Used by grab.js to decide what a hand can pick up.
 * @param {THREE.Vector3} point
 * @param {number} radius
 * @param {(h: BodyHandle) => boolean} [filter]
 * @returns {BodyHandle[]} Sorted nearest-first.
 */
export function bodiesNear(point, radius, filter) {
  const r2 = radius * radius;
  const out = [];
  for (const h of handles.values()) {
    if (h.removed) continue;
    if (filter && !filter(h)) continue;
    const t = h.body.translation();
    _v3b.set(t.x, t.y, t.z);
    const d2 = _v3b.distanceToSquared(point);
    if (d2 <= r2) out.push({ h, d2 });
  }
  out.sort((a, b) => a.d2 - b.d2);
  return out.map((e) => e.h);
}

/** @param {number} colliderHandle @returns {BodyHandle|undefined} */
export function handleForCollider(colliderHandle) {
  return byCollider.get(colliderHandle);
}

export default {
  init, isReady, step, syncMeshes, addBody, removeBody, removeByTag, clear,
  raycast, bodiesNear, onContactForce, getRAPIER, getWorld, bodyCount,
  allBodies, handleForCollider,
};
